import { SyntheticEvent, useCallback } from 'react';
import { AdaptoActions } from '@frontend/adapto-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { ContactActionPrefixes } from '@frontend/tracking-prefixes';
import { ActionButton, PopoverMenu, usePopoverMenu } from '@frontend/design-system';
import { PhoneNumberPopover } from './phone-number-popover';
import { CallSelectionContext } from './types';
import { usePhoneCallAction } from './use-call-action';

export type CallButtonProps = {
  label?: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
};

export const CallButton = ({ label, context, trackingIdSuffix }: CallButtonProps) => {
  const { handleCallActionClick, disabled, disabledDetails } = usePhoneCallAction({
    context,
  });

  return (
    <div>
      <ActionButton
        onClick={handleCallActionClick}
        trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
        disabled={disabled}
        label={disabledDetails ? disabledDetails : label}
      >
        <Icon name='phone-call-small' />
      </ActionButton>
    </div>
  );
};

export type CallActionProps = {
  label: string;
  context: CallSelectionContext;
  trackingIdSuffix?: string;
  isInsidePopoverDropdown?: boolean;
  onClick?: () => void; // Optional onClick handler for additional functionality
};

export const CallAction = ({ label, context, trackingIdSuffix, onClick, isInsidePopoverDropdown }: CallActionProps) => {
  const { t } = useTranslation('phone-call');

  const { handleCallActionClick, disabled, disabledDetails, multipleContactNumbers } = usePhoneCallAction({
    context,
  });

  const { getItemProps, getTriggerProps, getMenuProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-end',
    omitSizeMiddleware: true,
  });

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const clickToCallFeatureFlag = getFeatureFlagValue('click-to-call-multiple-numbers');

  const hasMultipleNumbers = multipleContactNumbers?.length > 1 && clickToCallFeatureFlag;
  const canShowPoppover = hasMultipleNumbers && !isOpen && !isInsidePopoverDropdown;
  const showSubMenuDropdown = hasMultipleNumbers && isInsidePopoverDropdown;

  const handleOpenPopover = useCallback(() => {
    open();
  }, [open]);

  const handleCallAction = useCallback(
    (e?: SyntheticEvent, overrideNumber?: string) => {
      handleCallActionClick(e, overrideNumber);
      onClick?.();
    },
    [onClick, handleCallActionClick]
  );

  const handleClick = canShowPoppover ? handleOpenPopover : handleCallAction;

  const disabledText = isInsidePopoverDropdown ? t('Call') : disabledDetails;

  return (
    <>
      <div>
        <AdaptoActions.Action
          {...getTriggerProps()}
          onClick={handleClick}
          trackingId={`${ContactActionPrefixes.Call}:${trackingIdSuffix}`}
          disabled={disabled}
          icon='phone'
          label={disabledDetails ? disabledText : label}
          subMenuItems={
            showSubMenuDropdown
              ? ({ menu: { getItemProps: getSubMenuItemProps } }) => {
                  return (
                    <PhoneNumberPopover
                      getItemProps={getSubMenuItemProps}
                      data={multipleContactNumbers}
                      onClick={handleCallAction ?? (() => {})}
                      trackingIdSuffix='phone-portal-weave2.0-calls-btn-tablerowaction-call-item'
                    />
                  );
                }
              : undefined
          }
        />
      </div>
      {isOpen && (
        <PopoverMenu {...getMenuProps()}>
          <PhoneNumberPopover
            getItemProps={getItemProps}
            trackingIdSuffix={trackingIdSuffix}
            data={multipleContactNumbers}
            onClick={handleCallAction ?? (() => {})}
          />
        </PopoverMenu>
      )}
    </>
  );
};
